/**
 * ParallaxDeck Component - Rearchitected with Queue-Based State Machine
 * 
 * A 3D carousel-style deck for displaying user profiles with parallax effect.
 * Used in the segregation feature for Tinder-like A/B classification.
 * 
 * Architecture:
 * - Linear queue (not circular) - items removed after classification
 * - State machine: idle → animating → processing → idle
 * - Stable keys for consistent AnimatePresence behavior
 * - Index-based access (no circular wrapping)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { motion, useAnimationControls, AnimatePresence } from 'framer-motion';
import { Instagram, Twitter, Linkedin } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

/**
 * Profile data structure for deck cards
 */
export interface DeckProfile {
  id: string;
  name: string;
  age?: number;
  bio: string | null;
  photo: string;
  instagramUrl: string | null;
  twitterUrl: string | null;
  linkedinUrl: string | null;
}

/**
 * State machine states for card animations
 */
type CardState = 'idle' | 'animating' | 'processing';

/**
 * Preview Card - Simplified side cards (non-interactive)
 */
function PreviewCard({ profile }: { profile: DeckProfile }) {
  return (
    <motion.div 
      className="relative block pointer-events-none select-none"
      initial={{ opacity: 0.6, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative rounded-[24px] overflow-hidden shadow-lg aspect-[3/5] ring-1 ring-white/10 w-[350px]">
        <ImageWithFallback 
          src={profile.photo} 
          alt={profile.name} 
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white text-left">
          <div className="text-lg font-semibold drop-shadow">{profile.name}</div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Tinder Profile Card - Interactive center card with A/B buttons
 */
function TinderProfileCard({
  profile,
  onSwipeRight,
  onSwipeLeft,
  disabled,
}: {
  profile: DeckProfile;
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  disabled: boolean;
}) {
  const controls = useAnimationControls();
  const [localAnimating, setLocalAnimating] = useState(false);

  // Reset animation when profile changes
  useEffect(() => {
    controls.set({ x: 0, rotate: 0, opacity: 1, scale: 1, y: 0 });
    setLocalAnimating(false);
  }, [controls, profile.id]);

  const swipeDir = (dir: 'left' | 'right') => {
    if (localAnimating || disabled) return;
    
    setLocalAnimating(true);
    const target = dir === 'right' ? 650 : -650;
    
    // Trigger callback IMMEDIATELY (non-blocking)
    // This allows parent to update state and show next card instantly
    const callback = dir === 'right' ? onSwipeRight : onSwipeLeft;
    if (callback) {
      callback(); // Don't await - let it run in background
    }
    
    // Animate card out in parallel (non-blocking)
    controls.start({
      x: target,
      rotate: dir === 'right' ? 25 : -25,
      opacity: 0,
      scale: 0.7,
      y: 20,
      transition: { 
        type: 'spring', 
        stiffness: 250, 
        damping: 30,
        mass: 0.8
      },
    });
    
    // Animation state will reset when new profile arrives via useEffect (line 84-87)
  };

  return (
    <div className="relative pointer-events-auto">
      <motion.div
        className="relative rounded-[28px] overflow-hidden aspect-[3/5] select-none ring-1 ring-white/15 w-[450px]"
        animate={controls}
        initial={{ scale: 1, opacity: 1, y: 0 }}
        whileHover={!disabled ? { scale: 1.02, transition: { duration: 0.2 } } : {}}
        transition={{ 
          type: 'spring', 
          stiffness: 280, 
          damping: 28,
          mass: 0.6 
        }}
      >
        {/* Image Section - Top 65% */}
        <div className="h-[65%] w-full relative overflow-hidden">
          <ImageWithFallback
            src={profile.photo}
            alt={profile.name}
            className="h-full w-full object-cover translate-y-[-5%]"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, black 86%, rgba(0,0,0,0) 100%)',
              maskImage: 'linear-gradient(to bottom, black 86%, rgba(0,0,0,0) 100%)',
            }}
            draggable={false}
          />
        </div>

        {/* Glass Panel - Bottom 44% */}
        <div className="absolute top-[56%] left-0 right-0 h-[44%] p-0">
          <div className="relative h-full w-full rounded-t-[32px] overflow-hidden">
            {/* Glassmorphism background layers */}
            <div className="absolute inset-0 bg-slate-900/55" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/45 to-slate-900/30" />
            <div className="absolute inset-0 backdrop-blur-3xl backdrop-saturate-50 ring-1 ring-white/15" />
            
            {/* Seamless blend with image fade */}
            <div
              className="pointer-events-none absolute -top-10 left-0 right-0 h-16"
              style={{
                WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, black 45%, black 100%)',
                maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, black 45%, black 100%)',
                background: 'linear-gradient(to bottom, rgba(15,23,42,0.65), rgba(15,23,42,0.85))',
              }}
            />

            {/* Content */}
            <div className="relative h-full p-5 flex flex-col justify-between">
              <div>
                {/* Name and Social Links */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-3xl font-semibold text-white/95 drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]">
                      {profile.name}{' '}
                      {profile.age ? <span className="font-medium">{profile.age}</span> : null}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {profile.instagramUrl && (
                      <motion.a
                        href={profile.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="grid place-items-center h-9 w-9 rounded-full bg-white/10 backdrop-blur-xl ring-1 ring-white/25 shadow-md"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ 
                          scale: 1.15, 
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transition: { duration: 0.15 }
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Instagram className="h-5 w-5 text-white/90" />
                      </motion.a>
                    )}
                    {profile.twitterUrl && (
                      <motion.a
                        href={profile.twitterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="grid place-items-center h-9 w-9 rounded-full bg-white/10 backdrop-blur-xl ring-1 ring-white/25 shadow-md"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ 
                          scale: 1.15, 
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transition: { duration: 0.15 }
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Twitter className="h-5 w-5 text-white/90" />
                      </motion.a>
                    )}
                    {profile.linkedinUrl && (
                      <motion.a
                        href={profile.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="grid place-items-center h-9 w-9 rounded-full bg-white/10 backdrop-blur-xl ring-1 ring-white/25 shadow-md"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ 
                          scale: 1.15, 
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transition: { duration: 0.15 }
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Linkedin className="h-5 w-5 text-white/90" />
                      </motion.a>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {profile.bio && (
                  <p className="mt-3 text-[15px] leading-5 text-white/85 line-clamp-4">{profile.bio}</p>
                )}
              </div>

              {/* A/B Buttons */}
              <div className="mt-4 flex items-center justify-center gap-5 pb-3">
                <div className="flex flex-col items-center gap-2">
                  <motion.button
                    aria-label="Classify as B (Left Arrow)"
                    onClick={() => swipeDir('left')}
                    disabled={localAnimating || disabled}
                    className="grid h-14 w-14 place-items-center rounded-full bg-white/10 backdrop-blur-2xl ring-1 ring-white/25 shadow-[0_8px_30px_rgba(0,0,0,0.25)] text-xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ 
                      scale: (localAnimating || disabled) ? 1 : 1.1, 
                      backgroundColor: 'rgba(255,255,255,0.22)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                      transition: { duration: 0.15 }
                    }}
                    whileTap={{ 
                      scale: (localAnimating || disabled) ? 1 : 0.92,
                      transition: { duration: 0.1 }
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    B
                  </motion.button>
                  <span className="text-xs text-white/60 font-medium">←</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <motion.button
                    aria-label="Classify as A (Right Arrow)"
                    onClick={() => swipeDir('right')}
                    disabled={localAnimating || disabled}
                    className="grid h-14 w-14 place-items-center rounded-full bg-white/10 backdrop-blur-2xl ring-1 ring-white/25 shadow-[0_8px_30px_rgba(0,0,0,0.25)] text-xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ 
                      scale: (localAnimating || disabled) ? 1 : 1.1, 
                      backgroundColor: 'rgba(255,255,255,0.22)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                      transition: { duration: 0.15 }
                    }}
                    whileTap={{ 
                      scale: (localAnimating || disabled) ? 1 : 0.92,
                      transition: { duration: 0.1 }
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    A
                  </motion.button>
                  <span className="text-xs text-white/60 font-medium">→</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * ParallaxDeck - Main 3D carousel component with queue-based state machine
 * 
 * State Machine:
 * - idle: Ready for user input
 * - animating: Card swipe animation in progress
 * - processing: Backend API call in progress
 * 
 * Queue Logic:
 * - profiles[index] = current card (center)
 * - profiles[index + 1, 2, 3] = next cards (right preview)
 * - profiles[index - 1, -2, -3] = previous cards (left preview) - only if index > 0
 * - When card removed: index stays same, profiles[index] becomes next card
 */
export default function ParallaxDeck({
  profiles,
  index,
  onChoose,
}: {
  profiles: DeckProfile[];
  index: number;
  onChoose: (profileId: string, selection: 'A' | 'B') => Promise<void>;
}) {
  const [cardState, setCardState] = useState<CardState>('idle');
  
  // Validation: ensure index is within bounds
  const validIndex = Math.max(0, Math.min(index, profiles.length - 1));
  const hasProfiles = profiles.length > 0;
  const currentProfile = hasProfiles ? profiles[validIndex] : null;

  // Calculate visible card indices (center + up to 3 on each side)
  const getVisibleIndices = (): number[] => {
    if (!hasProfiles) return [];
    
    const indices: number[] = [];
    const radius = 3; // Show 3 cards on each side
    
    for (let offset = -radius; offset <= radius; offset++) {
      const targetIndex = validIndex + offset;
      // Only include indices that exist in the array (no wrapping)
      if (targetIndex >= 0 && targetIndex < profiles.length) {
        indices.push(targetIndex);
      }
    }
    
    return indices;
  };

  const visibleIndices = getVisibleIndices();

  // Handle selection with state machine
  const handleSelect = useCallback(async (selection: 'A' | 'B') => {
    if (cardState !== 'idle' || !currentProfile) {
      console.warn(`Cannot select: state=${cardState}, hasProfile=${!!currentProfile}`);
      return;
    }
    
    setCardState('animating');
    
    try {
      // Wait for parent to process (animation happens in TinderProfileCard)
      await onChoose(currentProfile.id, selection);
      setCardState('idle');
    } catch (error) {
      console.error('Selection failed:', error);
      setCardState('idle');
    }
  }, [cardState, currentProfile, onChoose]);

  const handleRight = useCallback(() => handleSelect('A'), [handleSelect]);
  const handleLeft = useCallback(() => handleSelect('B'), [handleSelect]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      if (cardState !== 'idle') return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleLeft();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleRight();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleLeft, handleRight, cardState]);

  // Configuration for 3D parallax effect
  const gap = 200; // Horizontal spacing
  const rotateYStep = 12; // Rotation per card
  const scaleStep = 0.06; // Scale reduction per card
  const depth = 175; // Z-depth per card
  
  // Early return for empty deck
  if (!hasProfiles || !currentProfile) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-white/70">
          <p className="text-xl font-semibold">No more profiles to review</p>
          <p className="text-sm mt-2">Switch to Bucket A or B to review classifications</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-full pointer-events-none"
      style={{ 
        perspective: '1200px',
        transformStyle: 'preserve-3d',
        perspectiveOrigin: '50% 50%',
        zIndex: 1000
      }}
    >
      <AnimatePresence mode="popLayout">
        {visibleIndices.map((cardIndex) => {
          const profile = profiles[cardIndex];
          if (!profile) return null;

          // Calculate offset from center
          const offset = cardIndex - validIndex;
          const isCenter = offset === 0;
          
          // Position calculations
          const translateX = gap * offset;
          const scale = Math.max(0.7, 1 - Math.abs(offset) * scaleStep);
          const rotateY = offset * rotateYStep;
          const opacity = Math.max(0.15, 1 - Math.abs(offset) * 0.18);
          const zIndex = 100 - Math.abs(offset);
          const z = -Math.abs(offset) * depth;

          return (
            <motion.div
              key={profile.id}
              className="absolute pointer-events-auto"
              style={{
                left: '50%',
                top: '50%',
                zIndex,
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                transformOrigin: 'center center',
              }}
              initial={{
                x: `calc(-50% + ${translateX}px)`,
                y: '-50%',
                scale: scale * 0.8,
                rotateY,
                z: z - 50,
                opacity: 0,
              }}
              animate={{
                x: `calc(-50% + ${translateX}px)`,
                y: '-50%',
                scale,
                rotateY,
                z,
                opacity,
                filter: `blur(${Math.max(Math.abs(offset) - 1, 0) * 1.2}px)`,
              }}
              exit={{
                scale: scale * 0.8,
                opacity: 0,
                z: z - 50,
                transition: { duration: 0.25 }
              }}
              transition={{ 
                type: 'spring', 
                stiffness: 280, 
                damping: 35,
                mass: 0.7,
                restDelta: 0.001
              }}
            >
              {isCenter ? (
                <TinderProfileCard 
                  profile={profile} 
                  onSwipeRight={handleRight} 
                  onSwipeLeft={handleLeft}
                  disabled={cardState !== 'idle'}
                />
              ) : (
                <PreviewCard profile={profile} />
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
